import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../lib/i18n';
import { ArrowLeft, Trash2, Mail } from 'lucide-react';

export default function DeleteAccount() {
  const { lang } = useT();
  const isFr = lang === 'fr';

  return (
    <div className="min-h-screen bg-white" data-testid="delete-account-page">
      <header className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-[#E5DCC9] z-10 pt-safe">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm font-bold text-gray-600"
            data-testid="back-home-link"
          >
            <ArrowLeft size={16} /> {isFr ? 'Retour' : 'Back'}
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-4">
          <Trash2 size={20} className="text-[#7A1F2B]" />
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">
            {isFr ? 'Suppression de compte' : 'Account deletion'}
          </div>
        </div>

        <h1 className="font-heading text-4xl font-black tracking-tighter mb-2">
          {isFr ? 'Supprimer votre compte' : 'Delete your account'}
        </h1>
        <p className="text-xs text-gray-500 mb-8">
          {isFr
            ? 'SoumissionPro · Mise à jour : 23 mai 2026'
            : 'SoumissionPro · Updated: May 23, 2026'}
        </p>

        {isFr ? (
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <section>
              <h2 className="font-heading text-lg font-black mb-2">Méthode 1 — Directement dans l'application</h2>
              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li>Connectez-vous à votre compte SoumissionPro</li>
                <li>Allez dans <strong>Profil</strong> (icône en haut à droite)</li>
                <li>Faites défiler jusqu'à la section <strong>« Zone de danger »</strong></li>
                <li>Cliquez sur <strong>« Supprimer mon compte »</strong></li>
                <li>Confirmez la suppression — votre compte et toutes vos données seront supprimés immédiatement</li>
              </ol>
            </section>

            <section>
              <h2 className="font-heading text-lg font-black mb-2">Méthode 2 — Demande par courriel</h2>
              <p className="text-sm mb-3">
                Si vous ne pouvez plus vous connecter, envoyez un courriel à :
              </p>
              <a
                href="mailto:support@soumissionpro.com?subject=Demande%20de%20suppression%20de%20compte"
                className="inline-flex items-center gap-2 bg-[#7A1F2B] text-white px-4 py-3 rounded-xl font-bold text-sm hover:bg-[#5e1721] transition-colors"
                data-testid="email-delete-link"
              >
                <Mail size={16} /> support@soumissionpro.com
              </a>
              <p className="text-xs text-gray-500 mt-3">
                Indiquez l'adresse courriel associée à votre compte. Nous traiterons votre demande dans un délai de 7 jours ouvrables.
              </p>
            </section>

            <section className="bg-[#F5EFE0] border border-[#E5DCC9] rounded-2xl p-5">
              <h2 className="font-heading text-base font-black mb-3">Quelles données sont supprimées ?</h2>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Votre profil utilisateur (nom, courriel, informations d'entreprise)</li>
                <li>Toutes vos soumissions et brouillons</li>
                <li>Votre liste de clients</li>
                <li>Toutes les photos téléversées</li>
                <li>L'historique des signatures et PDF générés</li>
                <li>Votre abonnement actif (annulé automatiquement)</li>
              </ul>
            </section>

            <section className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <h2 className="font-heading text-base font-black mb-2 text-amber-900">Données conservées</h2>
              <p className="text-sm text-amber-900">
                Conformément à la <strong>Loi 25 du Québec</strong> et aux obligations comptables canadiennes, nous conservons les factures et reçus de paiement pendant <strong>6 ans</strong> à des fins fiscales et légales. Ces données sont anonymisées et ne sont accessibles qu'aux autorités compétentes sur demande.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-lg font-black mb-2">Combien de temps cela prend-il ?</h2>
              <p className="text-sm">
                <strong>Méthode 1 (dans l'app) :</strong> Suppression immédiate.<br />
                <strong>Méthode 2 (courriel) :</strong> Sous 7 jours ouvrables.
              </p>
            </section>

            <div className="pt-6 border-t border-gray-200 text-xs text-gray-500">
              <p>
                Pour toute question, contactez-nous à{' '}
                <a href="mailto:support@soumissionpro.com" className="text-[#7A1F2B] font-bold">
                  support@soumissionpro.com
                </a>
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <section>
              <h2 className="font-heading text-lg font-black mb-2">Method 1 — Directly in the app</h2>
              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li>Log in to your SoumissionPro account</li>
                <li>Go to <strong>Profile</strong> (top-right icon)</li>
                <li>Scroll to the <strong>"Danger Zone"</strong> section</li>
                <li>Click <strong>"Delete my account"</strong></li>
                <li>Confirm the deletion — your account and all data will be removed immediately</li>
              </ol>
            </section>

            <section>
              <h2 className="font-heading text-lg font-black mb-2">Method 2 — Email request</h2>
              <p className="text-sm mb-3">
                If you can no longer log in, send an email to:
              </p>
              <a
                href="mailto:support@soumissionpro.com?subject=Account%20Deletion%20Request"
                className="inline-flex items-center gap-2 bg-[#7A1F2B] text-white px-4 py-3 rounded-xl font-bold text-sm hover:bg-[#5e1721] transition-colors"
                data-testid="email-delete-link"
              >
                <Mail size={16} /> support@soumissionpro.com
              </a>
              <p className="text-xs text-gray-500 mt-3">
                Provide the email address associated with your account. We will process your request within 7 business days.
              </p>
            </section>

            <section className="bg-[#F5EFE0] border border-[#E5DCC9] rounded-2xl p-5">
              <h2 className="font-heading text-base font-black mb-3">What data is deleted?</h2>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Your user profile (name, email, business info)</li>
                <li>All your quotes and drafts</li>
                <li>Your client list</li>
                <li>All uploaded photos</li>
                <li>Signature history and generated PDFs</li>
                <li>Your active subscription (auto-cancelled)</li>
              </ul>
            </section>

            <section className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <h2 className="font-heading text-base font-black mb-2 text-amber-900">Retained data</h2>
              <p className="text-sm text-amber-900">
                In accordance with <strong>Quebec Law 25</strong> and Canadian accounting obligations, we retain invoices and payment receipts for <strong>6 years</strong> for tax and legal purposes. This data is anonymized and only accessible to competent authorities upon request.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-lg font-black mb-2">How long does it take?</h2>
              <p className="text-sm">
                <strong>Method 1 (in-app):</strong> Immediate deletion.<br />
                <strong>Method 2 (email):</strong> Within 7 business days.
              </p>
            </section>

            <div className="pt-6 border-t border-gray-200 text-xs text-gray-500">
              <p>
                For any questions, contact us at{' '}
                <a href="mailto:support@soumissionpro.com" className="text-[#7A1F2B] font-bold">
                  support@soumissionpro.com
                </a>
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
